import { Component, OnDestroy, computed, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card';
import { MatDivider } from '@angular/material/divider';
import { MatError } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatTooltip } from '@angular/material/tooltip';
import { webDtos } from '@vectronic/openApi-admin';
import { HeaderFieldComponent, HeaderGroupComponent, HeaderItemComponent } from '@vectronic/ui';
import { InternationalDateFormat, utcMoment } from '@vectronic/util';
import { cloneDeep } from 'es-toolkit';
import moment from 'moment';
import { Subscription, combineLatest } from 'rxjs';
import { AbstractScheduleService } from '../../services/abstract-schedule.service';
import { SequenceComponent } from '../sequence/sequence.component';
import {
  MAX_DATE,
  MIN_DATE,
  REPEAT_INTERVAL_TYPE_OPTION,
  RepeatIntervalType,
  SKIP_TYPE_OPTIONS,
  SkipType,
  TIME_TYPE_OPTIONS,
  TimeType,
} from './rule-config';
import {
  convertPeriodLengthToRepeatIntervalDay,
  convertPeriodLengthToRepeatIntervalHour,
  convertPeriodLengthToRepeatIntervalMinute,
  convertPeriodLengthToRepeatIntervalType,
  convertSkipCountToSkipType,
} from './rule-utils';

@Component({
  selector: 'rc-rule',
  templateUrl: './rule.component.html',
  styleUrls: ['./rule.component.scss'],
  providers: [provideMomentDateAdapter(InternationalDateFormat, { useUtc: true })],
  imports: [
    MatCard,
    MatCardTitle,
    MatIconButton,
    MatIcon,
    HeaderGroupComponent,
    HeaderItemComponent,
    HeaderFieldComponent,
    MatError,
    MatDivider,
    MatCardContent,
    SequenceComponent,
    MatButton,
    MatTooltip,
    MatRadioGroup,
    MatRadioButton,
    ReactiveFormsModule,
  ],
})
export class RuleComponent implements OnDestroy {
  index = input<number>();
  readonly = input<boolean>();
  scheduleService = input<AbstractScheduleService>();
  hasTransmissionCount = input<boolean>();
  maxTransmissionCount = input<number>();
  order = input<number>();
  hasDefaultSkipCount = input(true);
  hideChangedSchedules = input<boolean>();
  isRulesDeletable = input<boolean>();
  permanentStartDate = input<string>('2000-01-01');
  permanentEndDate = input<string>('2050-01-01');
  isNoCommunication = input<boolean>(false);

  remove = output<number>();
  rule = signal<webDtos.ScheduleRule>({
    startTime: this.permanentStartDate(),
    endTime: this.permanentEndDate(),
    skipCount: 0,
    periodLength: 24 * 60,
    sequences: [],
  });
  readonly repeatIntervalTypeFormControl = new FormControl<RepeatIntervalType>('daily');
  readonly timeTypeFormControl = new FormControl<TimeType>('permanent');
  readonly skipTypeFormControl = new FormControl<SkipType>(undefined);
  readonly startDateFormControl = new FormControl(
    moment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).format('YYYY-MM-DD'),
  );

  readonly endDateFormControl = new FormControl(
    moment()
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      .add({ year: 25 })
      .format('YYYY-MM-DD'),
  );
  readonly repeatIntervalDayFormControl = new FormControl(1);
  readonly repeatIntervalHourFormControl = new FormControl(0);
  readonly repeatIntervalMinuteFormControl = new FormControl(0);

  readonly skipPositionCountFormControl = new FormControl<number>(
    { value: undefined, disabled: this.skipTypeFormControl.value !== 'skip' },
    [Validators.required, Validators.min(1), Validators.max(96)],
  );
  readonly transmissionTypeFormControl = new FormControl<'count' | 'no_transmission'>('count');
  readonly transmissionFormControl = new FormControl<number>(1, [Validators.min(1)]);
  protected readonly REPEAT_INTERVAL_TYPE_OPTION = REPEAT_INTERVAL_TYPE_OPTION;
  protected readonly TIME_TYPE_OPTIONS = TIME_TYPE_OPTIONS;
  protected readonly MIN_DATE = MIN_DATE;
  protected readonly MAX_DATE = MAX_DATE;

  removeSubscription: Subscription;
  rulesSubscription: Subscription;
  transmissionSubscription: Subscription;

  private syncTransmissionControls(service: AbstractScheduleService, idx: number) {
    const fixPerTransmission = service.fixPerTransmission$.value?.[idx];
    this.transmissionTypeFormControl.setValue(
      fixPerTransmission === 0 ? 'no_transmission' : 'count',
    );
    this.transmissionFormControl.setValue(fixPerTransmission || 1);

    if (this.isNoCommunication()) {
      this.transmissionTypeFormControl.disable();
      this.transmissionFormControl.disable();
      return;
    }

    if (!this.readonly()) {
      this.transmissionTypeFormControl.enable();
    }

    if (fixPerTransmission === 0) {
      this.transmissionFormControl.disable();
    } else if (!this.readonly()) {
      this.transmissionFormControl.enable();
    }
  }

  skipTypeOptions = computed(() => {
    if (this.hasDefaultSkipCount()) {
      return SKIP_TYPE_OPTIONS;
    }
    return SKIP_TYPE_OPTIONS.filter((skipTypeOption) => skipTypeOption.value !== 'default');
  });

  constructor() {
    combineLatest([
      toObservable(this.scheduleService),
      toObservable(this.index),
      toObservable(this.permanentEndDate),
      toObservable(this.isNoCommunication),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([service, idx, , isNoComm]) => {
        if (!service || idx === undefined || idx === null) return;
        this.initialUiUpdate();
        this.updateServiceRule();

        if (isNoComm) {
          this.transmissionTypeFormControl.disable();
          this.transmissionFormControl.disable();
        } else if (!this.readonly()) {
          this.transmissionTypeFormControl.enable();
          if (this.transmissionTypeFormControl.value === 'count') {
            this.transmissionFormControl.enable();
          }
        }

        this.rulesSubscription?.unsubscribe();
        this.rulesSubscription = service.rules$.subscribe(() => {
          this.initialUiUpdate();
        });

        this.transmissionSubscription?.unsubscribe();
        this.transmissionSubscription = service.fixPerTransmission$.subscribe(() => {
          this.syncTransmissionControls(service, idx);
        });

        this.syncTransmissionControls(service, idx);

        this.removeSubscription?.unsubscribe();
        this.removeSubscription = service.isRemovedSchedule$.subscribe((isRemoved) => {
          if (isRemoved) {
            this.resetRule();
          }
        });

        this.skipPositionCountFormControl.setValidators([
          Validators.required,
          Validators.min(1),
          Validators.max(service.config.maxSkipCount),
        ]);
      });
  }

  updateFormControls(rule: webDtos.ScheduleRule) {
    if (rule && Object.keys(rule).length) {
      this.rule.set(cloneDeep(rule));
      this.repeatIntervalTypeFormControl.setValue(
        convertPeriodLengthToRepeatIntervalType(rule.periodLength),
      );
      const isStartPermanent =
        rule.startTime === this.permanentStartDate() ||
        (this.permanentStartDate() && rule.startTime?.startsWith?.(this.permanentStartDate()));
      const isEndPermanent =
        rule.endTime === this.permanentEndDate() ||
        (this.permanentEndDate() && rule.endTime?.startsWith?.(this.permanentEndDate()));
      if (isStartPermanent && isEndPermanent) {
        this.timeTypeFormControl.setValue('permanent');
      } else {
        this.timeTypeFormControl.setValue('temporary');
      }
      this.skipTypeFormControl.setValue(convertSkipCountToSkipType(rule.skipCount));
      this.startDateFormControl.setValue(rule.startTime);
      this.endDateFormControl.setValue(rule.endTime);
      this.repeatIntervalDayFormControl.setValue(
        convertPeriodLengthToRepeatIntervalDay(rule.periodLength),
      );
      this.repeatIntervalHourFormControl.setValue(
        convertPeriodLengthToRepeatIntervalHour(rule.periodLength),
      );
      this.repeatIntervalMinuteFormControl.setValue(
        convertPeriodLengthToRepeatIntervalMinute(rule.periodLength),
      );
      this.skipPositionCountFormControl.setValue(rule.skipCount);
      if (rule.skipCount) {
        this.skipPositionCountFormControl.enable();
      }

      const service = this.scheduleService();
      const idx = this.index();
      if (service && idx !== undefined && idx !== null) {
        this.syncTransmissionControls(service, idx);
      }
    }
  }

  initialUiUpdate() {
    this.rule.update((r) => {
      const newRule = cloneDeep(r);
      newRule.endTime = this.permanentEndDate() || '2050-01-01';
      return newRule;
    });
    this.endDateFormControl.setValue(this.permanentEndDate() || '2050-01-01');
    this.timeTypeFormControl.setValue('permanent');
    const service = this.scheduleService();
    const idx = this.index();
    if (!service || idx === undefined || idx === null) return;
    const rule = service.rules$.value[idx];
    const defaultRule = service.defaultRules$.value[idx];
    if (this.hideChangedSchedules()) {
      if (!defaultRule || !Object.keys(defaultRule).length) {
        this.resetSequence();
      } else {
        this.updateFormControls(defaultRule);
      }
    } else {
      if (!rule || !Object.keys(rule).length) {
        this.resetSequence();
      } else {
        this.updateFormControls(rule);
      }
    }
  }

  updateServiceRule() {
    const service = this.scheduleService();
    const idx = this.index();
    if (!service || idx === undefined || idx === null) return;
    const rule = service.rules$.value[idx];
    const defaultRule = service.defaultRules$.value[idx];
    if (this.hideChangedSchedules()) {
      if (!defaultRule || !Object.keys(defaultRule).length) {
        service.updateRule(idx, cloneDeep(this.rule()), this.readonly());
        this.resetSequence();
      }
    } else {
      if (!rule || !Object.keys(rule).length) {
        service.updateRule(idx, cloneDeep(this.rule()), this.readonly());
        this.resetSequence();
      }
    }
  }

  onClickRemoveRule() {
    this.remove.emit(this.index());
  }

  onTimeTypeChanged(value: string | any) {
    this.timeTypeFormControl.setValue(value);
    if (value === 'permanent') {
      this.rule.update((r) => {
        const newRule = cloneDeep(r);
        newRule.startTime = this.permanentStartDate();
        newRule.endTime = this.permanentEndDate();
        return newRule;
      });
      this.updateRule();
      this.endDateFormControl.setErrors({
        isEndDateAfterStartDate: null,
      });
      this.endDateFormControl.updateValueAndValidity();
    } else {
      this.startDateFormControl.setValue(undefined);
      this.endDateFormControl.setValue(undefined);
      this.rule.update((r) => {
        const newRule = cloneDeep(r);
        newRule.startTime = undefined;
        newRule.endTime = undefined;
        return newRule;
      });
    }
  }

  onStartDateChanged(datePicker: Date | any) {
    this.rule.update((r) => {
      const newRule = cloneDeep(r);
      const startTime = moment(datePicker).format('YYYY-MM-DD');
      const endTime = this.endDateFormControl.value
        ? moment(this.endDateFormControl.value).format('YYYY-MM-DD')
        : startTime;
      newRule.startTime = startTime;
      newRule.endTime = endTime;
      return newRule;
    });
    this.endDateFormControl.setValue(this.rule().endTime);
    this.updateRule();
    if (!this.isEndDateAfterStartDate) {
      this.endDateFormControl.setErrors({
        isEndDateAfterStartDate: true,
      });
    } else {
      this.endDateFormControl.setErrors({
        isEndDateAfterStartDate: null,
      });
      this.endDateFormControl.updateValueAndValidity();
    }
  }

  onEndDateChanged(datePicker: Date | any) {
    this.rule.update((r) => {
      const newRule = cloneDeep(r);
      newRule.endTime = moment(datePicker).format('YYYY-MM-DD');
      return newRule;
    });
    this.updateRule();
    if (!this.isEndDateAfterStartDate) {
      this.endDateFormControl.setErrors({
        isEndDateAfterStartDate: true,
      });
    } else {
      this.endDateFormControl.setErrors({
        isEndDateAfterStartDate: null,
      });
      this.endDateFormControl.updateValueAndValidity();
    }
  }

  get isEndDateAfterStartDate(): boolean {
    return moment(this.endDateFormControl.value).isSameOrAfter(
      moment(this.startDateFormControl.value),
    );
  }

  onSkipPositionChanged(value: string | any) {
    this.skipTypeFormControl.setValue(value);
    if (value === 'default') {
      this.rule.update((r) => {
        const newRule = cloneDeep(r);
        newRule.skipCount = undefined;
        return newRule;
      });
      this.skipPositionCountFormControl.setValue(undefined);
      this.skipPositionCountFormControl.disable();
    } else if (value === 'no_skip') {
      this.rule.update((r) => {
        const newRule = cloneDeep(r);
        newRule.skipCount = 0;
        return newRule;
      });
      this.skipPositionCountFormControl.setValue(0);
      this.skipPositionCountFormControl.disable();
    } else {
      this.rule.update((r) => {
        const newRule = cloneDeep(r);
        newRule.skipCount = parseInt(this.skipPositionCountFormControl.value as any);
        return newRule;
      });
      this.skipPositionCountFormControl.enable();
    }
    this.updateRule();
  }

  onSkipCountChanged(value: string | any) {
    this.skipPositionCountFormControl.setValue(value);
    this.rule.update((r) => {
      const newRule = cloneDeep(r);
      newRule.skipCount = parseInt(value);
      return newRule;
    });
    this.updateRule();
  }

  onRepeatIntervalChanged(value: string | any) {
    switch (value) {
      case 'daily':
        this.rule.update((r) => {
          const newRule = cloneDeep(r);
          newRule.periodLength = 60 * 24;
          return newRule;
        });
        break;
      case 'weekly':
        this.rule.update((r) => {
          const newRule = cloneDeep(r);
          newRule.periodLength = 60 * 24 * 7;
          return newRule;
        });
        break;
      case 'advanced':
        const minutes = this.rule().periodLength % 60;
        const days = Math.floor(this.rule().periodLength / 1440);
        const hours = (this.rule().periodLength - days * 1440 - minutes) / 60;
        this.repeatIntervalDayFormControl.setValue(days);
        this.repeatIntervalHourFormControl.setValue(hours);
        this.repeatIntervalMinuteFormControl.setValue(minutes);
        break;
    }
    this.checkRepeatInterval();
    this.updateRule();
  }

  resetSequence() {
    const newRule = cloneDeep(this.rule());
    newRule.sequences = [];
    this.rule.set(newRule);
  }

  onRepeatIntervalDayChanged(value: string | any) {
    const newRule = cloneDeep(this.rule());
    newRule.periodLength =
      (parseInt(value) || 0) * 24 * 60 +
      (parseInt(this.repeatIntervalHourFormControl.value as any) || 0) * 60 +
      (parseInt(this.repeatIntervalMinuteFormControl.value as any) || 0);
    this.rule.set(newRule);
    this.checkRepeatInterval();
    this.updateRule();
  }

  onRepeatIntervalHourChanged(value: string | any) {
    const newRule = cloneDeep(this.rule());
    newRule.periodLength =
      (parseInt(this.repeatIntervalDayFormControl.value as any) || 0) * 24 * 60 +
      (parseInt(value) || 0) * 60 +
      (parseInt(this.repeatIntervalMinuteFormControl.value as any) || 0);
    this.rule.set(newRule);
    this.checkRepeatInterval();
    this.updateRule();
  }

  onRepeatIntervalMinuteChanged(value: string | any) {
    const newRule = cloneDeep(this.rule());
    newRule.periodLength =
      (parseInt(this.repeatIntervalDayFormControl.value as any) || 0) * 24 * 60 +
      (parseInt(this.repeatIntervalHourFormControl.value as any) || 0) * 60 +
      (parseInt(value) || 0);
    this.rule.set(newRule);
    this.checkRepeatInterval();
    this.updateRule();
  }

  checkRepeatInterval() {
    this.resetSequence();
    const periodLength = this.rule().periodLength;
    if (periodLength > 65535 || periodLength < 0) {
      this.repeatIntervalTypeFormControl.setErrors({
        isRepeatIntervalInvalid: true,
      });
    } else {
      this.repeatIntervalTypeFormControl.setErrors({
        isRepeatIntervalInvalid: null,
      });
      this.repeatIntervalTypeFormControl.updateValueAndValidity();
    }
  }

  onClickAddFixRateRule() {
    const service = this.scheduleService();
    if (!service || this.readonly()) return;
    service.addDefaultSequenceToRule(this.index());
  }

  onRemoveSequence(index: number) {
    const newRule = cloneDeep(this.rule());
    newRule.sequences.splice(index, 1);
    this.rule.set(newRule);
    this.updateRule();
  }

  getSkipString(): string {
    const service = this.scheduleService();
    if (!service.config.hasSkipCount) {
      return '';
    }
    const skipCount = this.rule()?.skipCount;
    if (skipCount === null || skipCount === undefined) {
      return 'Default Skip Count';
    }
    return this.skipTypeFormControl.value === 'no_skip' || !this.skipPositionCountFormControl.value
      ? 'Send Every Position'
      : `Skip ${this.skipPositionCountFormControl.value} positions`;
  }

  getRepeatString(): string {
    if (!this.repeatIntervalTypeFormControl.value) {
      return '';
    }
    if (this.repeatIntervalTypeFormControl.value === 'daily') return 'Daily';
    if (this.repeatIntervalTypeFormControl.value === 'weekly') return 'Weekly';
    return this.getDurationStringInMinute(this.rule()?.periodLength);
  }

  getDurationStringInMinute(value: number): string {
    if (value === 0) return '';
    const seconds = Number(value);
    const d = Math.floor(seconds / (60 * 24));
    const h = Math.floor((seconds % (60 * 24)) / 60);
    const m = Math.floor(seconds % 60);
    if (m && !h && !d) {
      return `${m} min`;
    }
    const dDisplay = d > 0 ? d + 'd ' : '';
    const hDisplay = h > 0 ? h + 'h ' : '';
    const mDisplay = m > 0 ? m + 'm ' : '';
    return (dDisplay + hDisplay + mDisplay).trim();
  }

  isDeletable() {
    if (this.isRulesDeletable()) return true;
    const service = this.scheduleService();
    return !!(service && service.rules$.value && Object.entries(service.rules$.value)?.length > 1);
  }

  onClickRule() {
    const service = this.scheduleService();
    const idx = this.index();
    if (!service || idx === undefined || idx === null) return;
    service.toggleRule(idx);
  }

  getDurationString(): string {
    if (!this.timeTypeFormControl.value) {
      return '';
    }
    if (this.timeTypeFormControl.value === 'permanent') {
      return 'Permanent';
    }
    const start = this.startDateFormControl.value
      ? utcMoment(this.startDateFormControl.value).format('YYYY-MM-DD')
      : '';
    const end = this.endDateFormControl.value
      ? utcMoment(this.endDateFormControl.value).format('YYYY-MM-DD')
      : '';
    return `${start} -> ${end}`;
  }

  updateRule() {
    const service = this.scheduleService();
    const idx = this.index();
    if (!service || idx === undefined || idx === null) return;
    service.updateRule(idx, cloneDeep(this.rule()), this.readonly());
  }

  resetRule() {
    this.resetSequence();
    this.rule.set({
      startTime: this.permanentStartDate(),
      endTime: this.permanentEndDate(),
      skipCount: 0,
      periodLength: 24 * 60,
      sequences: [],
    });
    this.repeatIntervalTypeFormControl.setValue('daily');
    this.timeTypeFormControl.setValue('permanent');
    this.skipTypeFormControl.setValue(this.hasDefaultSkipCount() ? 'default' : 'no_skip');
    this.startDateFormControl.setValue(this.permanentStartDate());
    this.endDateFormControl.setValue(this.permanentEndDate());

    this.repeatIntervalDayFormControl.setValue(1);
    this.repeatIntervalHourFormControl.setValue(0);
    this.repeatIntervalMinuteFormControl.setValue(0);
    this.transmissionTypeFormControl.setValue('count');
    this.transmissionFormControl.setValue(1);
  }

  onTransmissionTypeChanged(value: 'count' | 'no_transmission') {
    this.transmissionTypeFormControl.setValue(value);
    const service = this.scheduleService();
    const idx = this.index();
    if (!service || idx === undefined || idx === null) return;

    if (value === 'no_transmission') {
      this.transmissionFormControl.disable();
      const curr = cloneDeep(service.fixPerTransmission$.value);
      curr[idx] = 0;
      service.fixPerTransmission$.next(curr);
    } else {
      this.transmissionFormControl.enable();
      const curr = cloneDeep(service.fixPerTransmission$.value);
      curr[idx] = this.transmissionFormControl.value || 1;
      service.fixPerTransmission$.next(curr);
    }
  }

  onTransmissionChanged($event: number | any) {
    const service = this.scheduleService();
    const idx = this.index();
    if (!service || idx === undefined || idx === null) return;
    let parsed = parseInt($event, 10);
    if (isNaN(parsed)) parsed = 1;
    const max = this.maxTransmissionCount() ?? Number.POSITIVE_INFINITY;
    const clamped = Math.max(1, Math.min(parsed, max));

    this.transmissionFormControl.setValue(clamped);
    const curr = cloneDeep(service.fixPerTransmission$.value);
    curr[idx] = clamped;
    service.fixPerTransmission$.next(curr);
  }

  ngOnDestroy() {
    this.removeSubscription?.unsubscribe();
    this.rulesSubscription?.unsubscribe();
    this.transmissionSubscription?.unsubscribe();
  }
}
